import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class TktStickyHeader implements Plugin {
  private wrapper: HTMLElement;
  private sentinel: HTMLElement | null = null;
  private spacer: HTMLElement | null = null;
  private observer: IntersectionObserver | null = null;
  private mediaQuery: MediaQueryList;
  private handleResizeChange: (e: MediaQueryListEvent) => void;

  constructor(private readonly element: HTMLElement) {
    this.wrapper = element;
    this.mediaQuery = window.matchMedia("(max-width: 940px)");
    this.handleResizeChange = (e) => this.onResize(e.matches);
  }

  init(): void {
    this.sentinel = this.wrapper.closest(
      ".tkt-availability-layout__info-header",
    ) as HTMLElement | null;

    if (!this.sentinel) {
      this.sentinel = this.wrapper.parentElement;
    }

    if (!this.sentinel) return;

    this.spacer = document.createElement("div");
    this.spacer.className = "com-detailHeaderB2B-spacer";
    this.spacer.setAttribute("aria-hidden", "true");
    this.wrapper.parentNode?.insertBefore(
      this.spacer,
      this.wrapper.nextSibling,
    );

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.mediaQuery.matches) return;

        const entry = entries[0];
        if (!entry.isIntersecting) {
          this.activate();
        } else {
          this.deactivate();
        }
      },
      {
        threshold: 0,
        rootMargin: "0px",
      },
    );

    this.observer.observe(this.sentinel);

    this.mediaQuery.addEventListener("change", this.handleResizeChange);
    this.onResize(this.mediaQuery.matches);
  }

  private activate(): void {
    const rect = this.wrapper.getBoundingClientRect();
    if (this.spacer) {
      this.spacer.style.height = `${rect.height}px`;
      this.spacer.classList.add("is-active");
    }
    this.wrapper.classList.add("is-sticky");
  }

  private deactivate(): void {
    this.wrapper.classList.remove("is-sticky");
    if (this.spacer) {
      this.spacer.classList.remove("is-active");
      this.spacer.style.height = "0px";
    }
  }

  private onResize(isMobile: boolean): void {
    if (isMobile) {
      this.deactivate();
    }
  }

  destroy(): void {
    if (this.observer && this.sentinel) {
      this.observer.unobserve(this.sentinel);
      this.observer.disconnect();
    }
    this.mediaQuery.removeEventListener("change", this.handleResizeChange);
    this.deactivate();
    if (this.spacer && this.spacer.parentNode) {
      this.spacer.parentNode.removeChild(this.spacer);
    }
  }
}

PluginRegistry.register("tktStickyHeader", TktStickyHeader);
